import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, ShieldAlert } from 'lucide-react';
import { useEffect } from 'react';
import { api, ApiError, invalidatePrefix } from '../api/client';
import type { BatchRow, LimiterBucket, QueueJob, QueueSnapshot } from '../api/types';
import { Badge, StatusBadge, TypeBadge } from '../components/Badge';
import { Card, KeyValue, PageHeader } from '../components/Card';
import { ErrorBanner, LoadingBlock, WarningBanner } from '../components/Feedback';
import { Button } from '../components/Form';
import { DataTable, Mono } from '../components/Table';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import { ago, dateTime, money, num, seconds } from '../lib/format';

const QUEUE_PATH = '/admin-api/queue';

export function QueuePage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const queueQuery = useQuery<QueueSnapshot, ApiError>({
    queryKey: ['api', QUEUE_PATH],
    queryFn: ({ signal }) => api.get<QueueSnapshot>(QUEUE_PATH, signal),
    refetchInterval: 2_500,
    placeholderData: (prev) => prev,
    gcTime: 0,
    staleTime: 0,
    retry: 1,
  });

  // SSE: instantly invalidate on any purchase event so the next poll returns fresh data.
  useEffect(() => {
    const es = new EventSource('/admin-api/events');
    const refresh = () => {
      void invalidatePrefix(queryClient, '/admin-api/queue');
      void queryClient.invalidateQueries({ queryKey: ['api', '/admin-api/stats/overview'] });
    };
    es.addEventListener('queued', refresh);
    es.addEventListener('processing', refresh);
    es.addEventListener('ready', refresh);
    es.addEventListener('failed', refresh);
    es.addEventListener('canceled', refresh);
    es.addEventListener('refunded', refresh);
    es.addEventListener('sms', refresh);
    es.onerror = () => {
      // EventSource auto-reconnects; just keep polling as backup.
    };
    return () => es.close();
  }, [queryClient]);

  const q = queueQuery.data;

  const cancelJob = async (job: QueueJob) => {
    const ok = await confirm({
      title: 'Отменить покупку?',
      body: `Покупка ${job.purchase_id.slice(0, 8)}… для клиента «${job.client}» будет отменена.`,
      confirmLabel: 'Отменить',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.post(`/admin-api/purchases/${job.purchase_id}/cancel`);
      toast.success('Покупка отменена');
      void invalidatePrefix(queryClient, '/admin-api/queue');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка отмены');
    }
  };

  const unban = async (bucket: LimiterBucket) => {
    const ok = await confirm({
      title: 'Разбанить лимитер?',
      body: `Снять бан с ключа «${bucket.key}» (бан до ${dateTime(bucket.banned_until)}).`,
      confirmLabel: 'Разбанить',
    });
    if (!ok) return;
    try {
      await api.post(`/admin-api/queue/limiter/${encodeURIComponent(bucket.key)}/unban`);
      toast.success('Бан снят');
      void invalidatePrefix(queryClient, '/admin-api/queue');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка');
    }
  };

  const limiter: LimiterBucket[] = q?.limiter ?? [];
  const jobs: QueueJob[] = q?.jobs ?? [];
  const batches: BatchRow[] = q?.batches ?? [];
  const banned = limiter.filter((b) => b.banned_until);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Очередь"
        description={q ? `в очереди ${num(q.depth.queued)} · в работе ${num(q.depth.processing)}` : 'Загрузка…'}
        actions={
          <Badge tone={q?.leader ? 'success' : 'muted'}>
            {q?.leader ? 'leader' : 'follower'}
          </Badge>
        }
      />

      {queueQuery.error ? <ErrorBanner message={queueQuery.error.message} onRetry={() => queueQuery.refetch()} /> : null}

      {q?.guard ? (
        <WarningBanner
          title="Очередь остановлена"
          message={
            <span>
              Worker guard активен: <Mono>{q.guard}</Mono>. Новые задачи не обрабатываются.
            </span>
          }
        />
      ) : null}

      {banned.length > 0 ? (
        <Card title="Забаненные лимитер-бакеты" noPadding>
          <div className="divide-y divide-zinc-800">
            {banned.map((b) => (
              <div key={b.key} className="flex items-center justify-between gap-3 px-3 py-2.5 sm:px-4">
                <div className="min-w-0">
                  <Mono>{b.key}</Mono>
                  <div className="mt-0.5 text-xs text-red-400">бан до {dateTime(b.banned_until)}</div>
                </div>
                <Button size="sm" variant="secondary" icon={<Ban className="size-3.5" />} onClick={() => unban(b)}>
                  Разбанить
                </Button>
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Состояние очереди">
          {q ? (
            <KeyValue
              items={[
                { label: 'В очереди', value: num(q.depth.queued) },
                { label: 'В работе', value: num(q.depth.processing) },
                { label: 'Старейшая в очереди', value: seconds(q.depth.oldestQueuedAgeSeconds) },
                { label: 'Worker guard', value: q.guard ?? 'нет' },
                { label: 'Leader', value: q.leader ? 'да' : 'нет' },
                { label: 'Задач', value: num(jobs.length) },
              ]}
            />
          ) : (
            <LoadingBlock />
          )}
        </Card>

        <Card title="Лимитер" noPadding>
          {limiter.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-zinc-500">Нет данных</div>
          ) : (
            <DataTable<LimiterBucket>
              columns={[
                { key: 'key', header: 'Ключ', render: (r) => <Mono>{r.key}</Mono> },
                { key: 'tokens', header: 'Токены', align: 'right', render: (r) => `${num(r.tokens)}/${num(r.capacity)}` },
                { key: 'per_min', header: '/мин', align: 'right', hide: 'sm', render: (r) => num(r.per_min) },
                { key: 'banned', header: 'Бан', render: (r) =>
                  r.banned_until ? (
                    <button onClick={() => unban(r)} className="text-xs text-sky-400 hover:underline">
                      до {dateTime(r.banned_until)}
                    </button>
                  ) : (
                    <Badge tone="muted">нет</Badge>
                  ),
                },
              ]}
              rows={limiter}
              rowKey={(r) => r.key}
              minWidth="34rem"
            />
          )}
        </Card>
      </div>

      <Card title="Задачи" subtitle={`${num(jobs.length)} активных`} noPadding>
        <DataTable<QueueJob>
          columns={[
            { key: 'age', header: 'Возраст', width: '5rem', render: (r) => seconds(r.age_seconds) },
            { key: 'client', header: 'Клиент', hide: 'sm', render: (r) => <span className="truncate">{r.client}</span> },
            { key: 'project', header: 'Проект', render: (r) => (
              <div className="flex items-center gap-1.5">
                <TypeBadge type={r.type} />
                <span className="truncate">{r.project}</span>
              </div>
            ) },
            { key: 'status', header: 'Статус', render: (r) => <StatusBadge kind="purchase" status={r.status} /> },
            { key: 'attempts', header: 'Попытки', align: 'right', hide: 'md', width: '5rem', render: (r) => num(r.attempts) },
            { key: 'price', header: 'Цена', align: 'right', hide: 'lg', render: (r) => money(r.price) },
            { key: 'error', header: 'Ошибка', hide: 'md', render: (r) =>
              r.last_error ? <span className="block max-w-48 truncate text-xs text-red-400" title={r.last_error}>{r.last_error}</span> : <span className="text-zinc-500">—</span>,
            },
            { key: 'actions', header: '', width: '5rem', align: 'right', render: (r) =>
              r.status === 'queued' ? (
                <Button size="sm" variant="ghost" onClick={() => cancelJob(r)}>Отменить</Button>
              ) : null,
            },
          ]}
          rows={jobs}
          rowKey={(r) => r.purchase_id}
          loading={queueQuery.isLoading}
          error={queueQuery.error}
          onRetry={() => queueQuery.refetch()}
          emptyTitle="Очередь пуста"
          emptyDescription="Активных задач нет."
          minWidth="42rem"
        />
      </Card>

      <Card title="Последние батчи" subtitle={`${num(batches.length)} записей`} noPadding>
        <DataTable<BatchRow>
          columns={[
            { key: 'status', header: 'Статус', width: '5rem', render: (r) => <StatusBadge kind="batch" status={r.status} /> },
            { key: 'app_id', header: 'App', align: 'right', hide: 'sm', width: '4rem', render: (r) => <Mono>{r.app_id}</Mono> },
            { key: 'type', header: 'Тип', width: '4rem', hide: 'md', render: (r) => <TypeBadge type={r.type} /> },
            { key: 'requested', header: 'Запрошено', align: 'right', width: '5rem', render: (r) => num(r.requested) },
            { key: 'api_count', header: 'API', align: 'right', hide: 'sm', width: '4rem', render: (r) => num(r.api_count) },
            { key: 'ordernum', header: 'Ordernum', hide: 'lg', render: (r) => <Mono className="text-[10px]">{r.ordernum}</Mono> },
            { key: 'cost', header: 'Стоимость', align: 'right', hide: 'md', render: (r) => money(r.cost) },
            { key: 'error', header: 'Ошибка', hide: 'md', render: (r) =>
              r.error ? <span className="block max-w-48 truncate text-xs text-red-400" title={r.error}>{r.error}</span> : <span className="text-zinc-500">—</span>,
            },
            { key: 'created', header: 'Создан', align: 'right', hide: 'xl', width: '7rem', render: (r) => ago(r.created_at) },
          ]}
          rows={batches}
          rowKey={(r) => r.id}
          loading={queueQuery.isLoading && !batches.length}
          emptyTitle="Батчей нет"
          minWidth="38rem"
        />
      </Card>

      <div className="flex items-center gap-2 text-[11px] text-zinc-600">
        <ShieldAlert className="size-3" />
        Автообновление каждые 2.5 с + SSE-события.
      </div>
    </div>
  );
}
